import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Check, Users, Bus, Trophy, Handshake, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const donationAreas = [
  {
    icon: Trophy,
    title: 'Youth Participation & Registration',
    description: 'Covers meet entry fees, league registrations, and participation costs so children can compete without financial stress on their families.',
  },
  {
    icon: Check,
    title: 'Equipment & Uniforms',
    description: 'Provides essential gear such as uniforms, shoes, backpacks, warm-ups, and safety equipment to ensure every child shows up prepared and confident.',
  },
  {
    icon: Bus,
    title: 'Travel & Event Access',
    description: 'Helps fund transportation, lodging assistance, and travel support for children to attend local, regional, and national events—especially for families without reliable transportation.',
  },
  {
    icon: Users,
    title: 'Program & Event Operations',
    description: 'Supports the costs of hosting and operating youth athletic events, including facility rentals, permits, timing services, medals, and insurance.',
  },
  {
    icon: Heart,
    title: 'Coaching, Mentorship & Development',
    description: 'Invests in trained coaches, mentors, and volunteers who provide instruction, guidance, and positive role modeling both on and off the track.',
  },
  {
    icon: Handshake,
    title: 'Community Outreach & Family Support',
    description: 'Extends programs into underserved communities, partners with schools and local organizations, and offers resources that support the whole family, not just the athlete.',
  },
];

const WhereYourDonationsGo = () => {
  return (
    <div className="pt-20" data-testid="where-donations-go-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold">
              Where Your Donations Go
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-3xl mx-auto">
              Every dollar donated to Lane 4 For All Kids Foundation is used to remove barriers and expand access to youth sports opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              Why Donate to Lane 4 All Kids Foundation
            </h2>
          </div>
          <div className="prose prose-slate prose-lg max-w-none">
            <p>Lane 4 All Kids Foundation exists to make sure a child's opportunity to participate in sports is never determined by their family's income, zip code, or circumstances. For too many kids, the cost of registration fees, equipment, travel, and uniforms becomes a barrier before they ever step onto the track or field. When that happens, children lose more than a chance to compete—they lose access to mentorship, discipline, confidence, and safe spaces that sports uniquely provide.</p>
            
            <p><strong>Your donation directly removes those barriers.</strong></p>
            
            <p>Lane 4 All Kids Foundation uses community-based sports, especially track and field, as a vehicle to:</p>
            <ul>
              <li>Expand access to organized youth athletics</li>
              <li>Provide essential resources like equipment, event entry, and support services</li>
              <li>Create positive outlets that promote physical health, mental resilience, and leadership</li>
              <li>Reach children in underserved and low-income communities who are often overlooked</li>
            </ul>
            
            <p>Every contribution helps place a child in Lane 4—the lane where opportunity meets preparation, and potential has room to run.</p>
            
            <p className="text-red-600 font-semibold">When you donate to Lane 4 All Kids Foundation, you are not just supporting sports. You are investing in confidence, equity, community, and the future of our youth.</p>
          </div>
        </div>
      </section>

      {/* Donation Areas */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="donation-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              How Your Donation Is Used
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {donationAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 border border-slate-200 hover:shadow-lg transition-shadow"
                data-testid={`donation-area-${index}`}
              >
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center mb-4">
                  <area.icon className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{area.title}</h3>
                <p className="mt-2 text-slate-600 text-sm">{area.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Statement */}
      <section className="py-16 md:py-20 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">
            Your support doesn't just fund sports—it fuels opportunity, builds confidence, and creates pathways for children to thrive.
          </h2>
          <div className="mt-8">
            <Link to="/donate" data-testid="donate-now-btn">
              <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
                Donate Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-300">
            <strong className="text-white">Lane 4 All Kids Foundation, Inc.</strong><br />
            877.495.2634 | info@lane4foundation.com
          </p>
        </div>
      </section>
    </div>
  );
};

export default WhereYourDonationsGo;
